/**
 * LegalTemplates PDF Editor — shell chrome: menus, tool groups, phone sheets
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { openSignatureModal } from './signature.js';
import { openBlanksSheet, openPrefillSheet } from './assist.js';
import {
    editSuiteModes,
    pagesEl,
    session,
    shellMenuBackdrop,
    shellMenuPanels,
    shellMenuTriggers,
    state,
    subbarEl,
    THUMBS_PREF_KEY,
    workspaceShellEl,
} from './context.js';
import { attachPageEvents, hasLoadedDocumentForActions } from './core.js';
import { openFindBar } from './find.js';
import { activateTool, setSuiteMode, showAlert } from './ui.js';
import { getZoom } from './viewport.js';

export function getPageShells() {
    return Array.from(pagesEl.querySelectorAll('.pdf-page-shell'));
}

export function isShellNearViewport(pageShell, margin = 400) {
    const rect = pageShell.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > -margin && rect.top < viewportHeight + margin;
}

export function closeShellMenus() {
    shellMenuPanels.forEach((panel) => {
        panel.hidden = true;
    });
    shellMenuTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
    });
    if (shellMenuBackdrop) {
        shellMenuBackdrop.hidden = true;
    }
    document.body.classList.remove('pdf-menu-open');
}

export function isPhoneShell() {
    return window.matchMedia('(max-width: 768px)').matches;
}

// Tool menus live inside the sub-bar so they anchor to their trigger on
// desktop. The sub-bar's backdrop blur makes it the containing block for
// fixed children, so on phones the sheet is moved to <body> while open.
export function placeShellPanel(panel, trigger = null) {
    if (!panel.__home) {
        panel.__home = panel.parentElement;
    }
    if (isPhoneShell()) {
        clearFloatingPanelStyles(panel);
        if (panel.parentElement !== document.body) {
            document.body.appendChild(panel);
        }
        return;
    }

    // The top bar's action strip scrolls sideways on narrow desktops, which
    // clips anything positioned inside it — its menus (Pages, Export, Open)
    // float from <body> under their trigger instead.
    const clippedHome = panel.__home ? panel.__home.closest('.pdf-editor-top-actions') : null;
    if (clippedHome && trigger) {
        if (panel.parentElement !== document.body) {
            document.body.appendChild(panel);
        }
        const rect = trigger.getBoundingClientRect();
        panel.classList.add('pdf-menu-panel--floating');
        panel.style.position = 'fixed';
        panel.style.top = `${Math.round(rect.bottom + 6)}px`;
        panel.style.right = `${Math.max(8, Math.round(window.innerWidth - rect.right))}px`;
        panel.style.left = 'auto';
        panel.style.maxHeight = `${Math.max(160, Math.round(window.innerHeight - rect.bottom - 16))}px`;
        bindFloatingPanelDismissal();
        return;
    }

    clearFloatingPanelStyles(panel);
    if (panel.__home && panel.parentElement !== panel.__home) {
        panel.__home.appendChild(panel);
    }
}

function clearFloatingPanelStyles(panel) {
    panel.classList.remove('pdf-menu-panel--floating');
    panel.style.position = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.left = '';
    panel.style.maxHeight = '';
}

// A floating panel is anchored to a viewport point; a resize moves the
// trigger, so the menu closes rather than drifting.
function bindFloatingPanelDismissal() {
    if (state.shellFloatingDismissBound) {
        return;
    }
    state.shellFloatingDismissBound = true;
    window.addEventListener('resize', () => closeShellMenus());
}

export function openShellMenu(name, trigger) {
    closeShellMenus();
    const panel = shellMenuPanels.find((candidate) => candidate.dataset.menuPanel === name);
    if (!panel) {
        return;
    }
    placeShellPanel(panel, trigger);
    panel.hidden = false;
    if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
    }
    // Every trigger for this panel (desktop group + phone tab) shows open.
    shellMenuTriggers
        .filter((candidate) => candidate.dataset.menuTrigger === name)
        .forEach((candidate) => candidate.setAttribute('aria-expanded', 'true'));
    if (shellMenuBackdrop) {
        shellMenuBackdrop.hidden = false;
    }
    document.body.classList.add('pdf-menu-open');
    syncShellMenuState(panel);
    const first = panel.querySelector('[role="menuitem"]:not([disabled])');
    if (first && typeof first.focus === 'function') {
        first.focus({ preventScroll: true });
    }
}

export function syncShellMenuState(panel) {
    const hasDocument = hasLoadedDocumentForActions();
    panel.querySelectorAll('[data-needs-document]').forEach((item) => {
        item.disabled = !hasDocument;
    });
    panel.querySelectorAll('[data-edit-tool]').forEach((item) => {
        item.classList.toggle('active', state.activeSuiteTool === 'edit' && item.dataset.editTool === state.activeTool);
    });
    panel.querySelectorAll('[data-thumbs-label]').forEach((label) => {
        label.textContent = areThumbnailsOpen() ? 'Hide thumbnails' : 'Show thumbnails';
    });
}

export function areThumbnailsOpen() {
    if (!workspaceShellEl) {
        return false;
    }
    const forced = workspaceShellEl.dataset.thumbs;
    if (forced === 'open') {
        return true;
    }
    if (forced === 'closed') {
        return false;
    }
    return window.matchMedia('(min-width: 1200px)').matches;
}

export function setThumbnailsOpen(open) {
    if (!workspaceShellEl) {
        return;
    }
    workspaceShellEl.dataset.thumbs = open ? 'open' : 'closed';
    try {
        window.localStorage.setItem(THUMBS_PREF_KEY, open ? 'open' : 'closed');
    } catch (error) {
        // Storage may be unavailable; the toggle still works for this page.
    }
    shellMenuPanels.forEach(syncShellMenuState);
}

export function restoreThumbnailPreference() {
    if (!workspaceShellEl) {
        return;
    }
    try {
        const stored = window.localStorage.getItem(THUMBS_PREF_KEY);
        if (stored === 'open' || stored === 'closed') {
            workspaceShellEl.dataset.thumbs = stored;
        }
    } catch (error) {
        // Ignore.
    }
}

export function syncToolGroups(tool) {
    const inEditMode = state.activeSuiteTool === 'edit';
    document.querySelectorAll('[data-tool-group]').forEach((group) => {
        const item = group.querySelector(`[data-edit-tool="${tool}"]`);
        const trigger = group.querySelector('.pdf-tool-group-trigger');
        const label = group.querySelector('.pdf-tool-group-label');
        const isActive = !!(item && inEditMode);
        group.classList.toggle('has-active', isActive);
        if (label) {
            const itemLabel = item ? item.querySelector('.pdf-menu-item-label') : null;
            label.textContent = isActive && itemLabel
                ? itemLabel.textContent
                : (group.dataset.groupLabel || label.textContent);
        }
        if (trigger) {
            trigger.classList.toggle('active', isActive);
        }
    });
    document.querySelectorAll('.pdf-mobile-tab').forEach((tab) => {
        const tabName = tab.dataset.mobileTab;
        const active = inEditMode && (
            (tabName === 'fill' && tool === 'form-fill') ||
            (tabName === 'text' && (tool === 'text' || tool === 'replace')) ||
            (tabName === 'annotate' && ['highlight', 'underline', 'draw', 'shape', 'comment', 'image'].includes(tool))
        );
        tab.classList.toggle('active', active);
    });
    if (subbarEl) {
        subbarEl.dataset.activeTool = inEditMode ? tool : '';
    }
    shellMenuPanels.forEach(syncShellMenuState);
}

export function syncShellMode(mode) {
    document.body.dataset.pdfMode = mode;
    document.querySelectorAll('.pdf-mobile-tab[data-mobile-tab="sign"]').forEach((tab) => {
        tab.classList.toggle('active', mode === 'signature');
    });
}

export async function runShellAction(action, el) {
    switch (action) {
        case 'suite': {
            const mode = el.dataset.suite;
            const openButton = el.dataset.open ? document.getElementById(el.dataset.open) : null;
            if (!mode) {
                return;
            }
            if (!hasLoadedDocumentForActions()) {
                showAlert('Upload a PDF first.', true);
                return;
            }
            setSuiteMode(mode);
            if (openButton) {
                openButton.click();
            }
            return;
        }
        case 'signature': {
            const signatureButton = document.querySelector('[data-suite-tool="signature"]');
            if (signatureButton) {
                signatureButton.click();
            }
            return;
        }
        case 'tool': {
            const tool = el.dataset.tool;
            if (!tool) {
                return;
            }
            state.userChoseTool = true;
            if (state.activeSuiteTool !== 'edit') {
                setSuiteMode('edit');
            }
            activateTool(tool);
            return;
        }
        case 'click': {
            const target = el.dataset.target ? document.getElementById(el.dataset.target) : null;
            if (target && !target.disabled) {
                target.click();
            }
            return;
        }
        case 'thumbs':
            setThumbnailsOpen(!areThumbnailsOpen());
            return;
        case 'edit-mode':
            setSuiteMode('edit');
            return;
        case 'find':
            openFindBar();
            return;
        case 'self-sign':
            openSignatureModal();
            return;
        case 'blanks':
            openBlanksSheet();
            return;
        case 'prefill':
            openPrefillSheet().catch(() => {});
            return;
        default:
            return;
    }
}

export function bindShellChrome() {
    document.body.classList.add('pdf-editor-body');
    restoreThumbnailPreference();

    shellMenuTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.stopPropagation();
            const name = trigger.dataset.menuTrigger;
            const panel = shellMenuPanels.find((candidate) => candidate.dataset.menuPanel === name);
            if (panel && !panel.hidden) {
                closeShellMenus();
            } else {
                openShellMenu(name, trigger);
            }
        });
    });

    shellMenuPanels.forEach((panel) => {
        panel.addEventListener('click', (event) => {
            event.stopPropagation();
            const item = event.target.closest('[data-edit-tool], [data-shell-action]');
            if (item) {
                // Let the item's own handler run first, then fold the menu.
                window.setTimeout(closeShellMenus, 0);
            }
        });
    });

    document.addEventListener('click', (event) => {
        if (!event.target.closest('[data-menu-panel], [data-menu-trigger]')) {
            closeShellMenus();
        }
    });

    if (shellMenuBackdrop) {
        shellMenuBackdrop.addEventListener('click', closeShellMenus);
    }

    document.querySelectorAll('[data-shell-action]').forEach((el) => {
        el.addEventListener('click', () => {
            runShellAction(el.dataset.shellAction, el).catch(() => {});
        });
    });

    // When a page-operation or export dialog closes, fall back to editing
    // so the toolbar returns. Modals toggle an `active` class.
    const suiteModals = Array.from(document.querySelectorAll('.pdf-convert-modal, [id$="-modal"]'))
        .filter((el) => el.classList.contains('pdf-convert-modal') || /modal/i.test(el.id));
    if (suiteModals.length && typeof MutationObserver === 'function') {
        const observer = new MutationObserver(() => {
            const anyOpen = suiteModals.some((modal) => modal.classList.contains('active'));
            if (!anyOpen && !editSuiteModes.has(state.activeSuiteTool)) {
                setSuiteMode('edit');
            }
        });
        suiteModals.forEach((modal) => {
            observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
        });
    }

    syncToolGroups(state.activeTool);
    syncShellMode(state.activeSuiteTool);
}

/** The Open menu's "This document" group when the menu exists (members). */
function openMenuLinksHost() {
    const host = document.querySelector('[data-open-menu-links]');
    if (!host) {
        return null;
    }
    if (!host.querySelector('.pdf-menu-heading')) {
        const heading = document.createElement('p');
        heading.className = 'pdf-menu-heading';
        heading.textContent = 'This document';
        host.appendChild(heading);
    }
    return host;
}

function menuLink(attribute, label, hint, icon) {
    const link = document.createElement('a');
    link.className = 'pdf-menu-item';
    link.setAttribute('role', 'menuitem');
    link.setAttribute(attribute, '1');
    link.innerHTML = `<span class="pdf-menu-item-icon" aria-hidden="true">${icon}</span>`
        + `<span class="pdf-menu-item-copy"><span class="pdf-menu-item-label">${label}</span><small>${hint}</small></span>`;
    return link;
}

export function ensureHistoryLink(url) {
    if (!url) {
        return;
    }
    let link = document.querySelector('[data-history-link]');
    if (!link) {
        const host = openMenuLinksHost();
        if (host) {
            link = menuLink('data-history-link', 'History', 'Earlier versions of this document', '&#8635;');
            host.appendChild(link);
        } else {
            const actionsEl = document.querySelector('.pdf-editor-top-actions');
            if (!actionsEl) {
                return;
            }
            link = document.createElement('a');
            link.className = 'pdf-editor-action pdf-topbar-desktop-only';
            link.setAttribute('data-history-link', '1');
            link.textContent = 'History';
            actionsEl.insertBefore(link, actionsEl.firstChild);
        }
    }
    link.href = url;
}

/**
 * Save buttons follow the e-sign lock: a locked dashboard document offers
 * "Save as New Copy" only; once the tab edits the copy, plain saves return.
 */
export function syncSaveButtonsForLock() {
    const saveButton = document.getElementById('editor-save');
    const saveCopyButton = document.getElementById('editor-save-copy');
    if (saveButton) {
        saveButton.textContent = session.lockedForDirectEdit ? 'Save as New Copy' : 'Save Edits';
    }
    if (saveCopyButton) {
        saveCopyButton.classList.toggle('pdf-force-hidden', session.lockedForDirectEdit);
    }
}

// Fixed chrome that must hang below the editor's sticky bars. Their CSS
// offsets assumed the bars start at the top of the viewport; with the site
// header above the editor on desktop they sat on the header instead. The
// anchors are sticky, so their live bottom edge is the right reference
// whether or not the header has scrolled away.
const ANCHORED_CHROME = [
    { selector: '#pdf-find-bar', below: '.pdf-editor-topbar', gap: 6 },
    { selector: '#pdf-props-panel', below: '#pdf-editor-subbar', gap: 10 },
];

export function repositionAnchoredChrome() {
    if (isPhoneShell()) {
        ANCHORED_CHROME.forEach(({ selector }) => {
            const el = document.querySelector(selector);
            if (el) {
                el.style.top = '';
            }
        });
        return;
    }
    ANCHORED_CHROME.forEach(({ selector, below, gap }) => {
        const el = document.querySelector(selector);
        if (!el || el.hidden) {
            return;
        }
        const anchor = document.querySelector(below);
        if (!anchor) {
            return;
        }
        const rect = anchor.getBoundingClientRect();
        el.style.top = `${Math.max(0, Math.round(rect.bottom + gap))}px`;
    });
}

export function bindChromeAnchoring() {
    // A short timer rather than requestAnimationFrame: rAF pauses in a
    // background tab, and a bar that stays pinned to a stale offset while
    // the page scrolls underneath is exactly the bug being fixed.
    const schedule = () => {
        if (state.chromeAnchorPending) {
            return;
        }
        state.chromeAnchorPending = true;
        window.setTimeout(() => {
            state.chromeAnchorPending = false;
            repositionAnchoredChrome();
        }, 16);
    };
    // Any scroller (the window on desktop, the canvas area elsewhere).
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    schedule();
}

export function ensureDashboardLink(url) {
    if (!url) {
        return;
    }
    let link = document.querySelector('[data-dashboard-link]');
    if (!link) {
        const host = openMenuLinksHost();
        if (host) {
            link = menuLink('data-dashboard-link', 'View in Dashboard', 'Saved to your documents', '&#8962;');
            const heading = host.querySelector('.pdf-menu-heading');
            host.insertBefore(link, heading ? heading.nextSibling : null);
        } else {
            const actionsEl = document.querySelector('.pdf-editor-top-actions');
            if (!actionsEl) {
                return;
            }
            link = document.createElement('a');
            link.className = 'pdf-editor-action';
            link.setAttribute('data-dashboard-link', '1');
            link.textContent = 'View in Dashboard';
            actionsEl.insertBefore(link, actionsEl.firstChild);
        }
    }
    link.href = url;
}

// Page-shell rectangle in base (unzoomed) units. Use this instead of a raw
// getBoundingClientRect() whenever the result feeds annotation geometry.
export function getPageShellRect(pageShell) {
    const rect = pageShell.getBoundingClientRect();
    const zoom = getZoom();
    return {
        left: rect.left,
        top: rect.top,
        width: rect.width / zoom,
        height: rect.height / zoom,
    };
}

export function createPageShell(pageNumber) {
    const pageShell = document.createElement('div');
    pageShell.className = 'pdf-page-shell';
    pageShell.dataset.page = String(pageNumber);
    pageShell.innerHTML = `
        <canvas class="pdf-page-canvas"></canvas>
        <svg class="pdf-annotation-layer" xmlns="http://www.w3.org/2000/svg"></svg>
    `;
    attachPageEvents(pageShell);
    return pageShell;
}
